import { useEffect, useRef, useState } from "react";
import * as Phaser from "phaser";
import { GameHUD } from "./GameHUD";
import { Leaderboard } from "./Leaderboard";
import { toast } from "sonner";

interface GameToken {
  id: string;
  x: number;
  y: number;
  color: string;
  letter: string;
  sprite?: Phaser.GameObjects.Graphics;
  text?: Phaser.GameObjects.Text;
  isTarget: boolean;
  tokenType: 'normal' | 'wrong-color' | 'wrong-letter' | 'bonus-life' | 'bonus-freeze' | 'bonus-score';
  bonusValue?: number;
  size?: number;
}

const COLORS = ['#A855F7', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9A8B', '#A8E6CF'];
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];

// VIVACLUB progression system
const VIVACLUB_SEQUENCE = ['V', 'I', 'V', 'A', 'C', 'L', 'U', 'B'];
const TARGET_COLORS = ['#A855F7', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9A8B', '#A8E6CF'];

interface VivaClubGameProps {
  onGameEnd?: (score: number) => void;
  onGameStart?: () => Promise<void>;
  dailyPlaysRemaining?: number; // Number of plays remaining today
}

export const VivaClubGame = ({ onGameEnd, onGameStart, dailyPlaysRemaining = 1 }: VivaClubGameProps) => {
  const gameRef = useRef<HTMLDivElement>(null);
  const phaserGame = useRef<Phaser.Game | null>(null);
  const gameScene = useRef<Phaser.Scene | null>(null);
  
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameState, setGameState] = useState<'waiting' | 'playing' | 'gameOver'>('waiting');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [currentTargetIndex, setCurrentTargetIndex] = useState(0);
  const [currentTargetColor, setCurrentTargetColor] = useState(TARGET_COLORS[0]);
  const [currentTargetLetter, setCurrentTargetLetter] = useState(VIVACLUB_SEQUENCE[0]);
  
  const gameData = useRef({
    score: 0,
    lives: 3,
    gameState: 'waiting' as 'waiting' | 'playing' | 'gameOver',
    tokens: [] as GameToken[],
    spawnTimer: 0,
    spawnInterval: 1500,
    lastTargetSpawnTime: 0,
    targetSpawnCooldown: 5000, // 5 seconds
    speedMultiplier: 1,
    startTime: 0,
    tokenStartTimes: new Map<string, number>(),
    currentTargetIndex: 0,
    currentTargetColor: TARGET_COLORS[0],
    currentTargetLetter: VIVACLUB_SEQUENCE[0],
    targetTokenOnScreen: false, // Track if target token is currently visible
    targetMissed: false, // Track if target was missed
    mustShowTarget: false, // Force target to appear within 5 seconds
    lastTargetDeadline: 0, // When the next target must appear by
    vivaClubColors: TARGET_COLORS.slice(0, 8), // Fixed colors for each letter in VIVACLUB
    // New game features
    consecutiveHits: 0,
    scoreMultiplier: 1,
    level: 1,
    tokensCollected: 0,
    baseTokenSize: 35,
    currentTokenSize: 35,
    freezeTimeUntil: 0,
    audioContext: null as AudioContext | null
  });

  // GameScene class removed - using inline scene object in Phaser config instead

  // Simple audio system for sound effects
  const initializeAudio = () => {
    try {
      // Create audio context for simple beep sounds
      if (!gameData.current.audioContext) {
        gameData.current.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
    } catch (error) {
      console.log('Audio not available:', error);
    }
  };

  const playSound = (frequency: number, duration: number, volume: number = 0.1) => {
    if (!gameData.current.audioContext) return;
    
    try {
      const oscillator = gameData.current.audioContext.createOscillator();
      const gainNode = gameData.current.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(gameData.current.audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, gameData.current.audioContext.currentTime);
      oscillator.type = 'square';
      
      gainNode.gain.setValueAtTime(0, gameData.current.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, gameData.current.audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, gameData.current.audioContext.currentTime + duration);
      
      oscillator.start(gameData.current.audioContext.currentTime);
      oscillator.stop(gameData.current.audioContext.currentTime + duration);
    } catch (error) {
      console.log('Sound playback failed:', error);
    }
  };

  const createToken = (x: number, y: number, forceTarget = false): GameToken => {
    let color, letter, isTarget;
    let tokenType: GameToken['tokenType'] = 'normal';
    let bonusValue: number | undefined;
    
    // Progressive token size based on level
    const currentLevel = Math.floor(gameData.current.tokensCollected / 8) + 1;
    const sizeReduction = Math.min(currentLevel * 2, 15); // Max 15px reduction
    const tokenSize = Math.max(20, gameData.current.baseTokenSize - sizeReduction);
    
    if (forceTarget) {
      color = gameData.current.currentTargetColor;
      letter = gameData.current.currentTargetLetter;
      isTarget = true;
      console.log('CREATING FORCED TARGET TOKEN:', { color, letter, targetColor: gameData.current.currentTargetColor });
    } else {
      // 15% chance for bonus tokens (rarer)
      const bonusChance = Math.random();
      if (bonusChance < 0.05) { // 5% extra life
        tokenType = 'bonus-life';
        color = '#FFD700'; // Gold
        letter = '♥';
        isTarget = false;
      } else if (bonusChance < 0.08) { // 3% freeze time
        tokenType = 'bonus-freeze';
        color = '#87CEEB'; // Sky blue
        letter = '❄';
        isTarget = false;
      } else if (bonusChance < 0.12) { // 4% score boost
        tokenType = 'bonus-score';
        color = '#32CD32'; // Lime green
        letter = '★';
        bonusValue = Math.floor(Math.random() * 5) + 3; // 3-7 points
        isTarget = false;
      } else {
        // 25% chance for wrong tokens (trickier)
        const wrongChance = Math.random();
        if (wrongChance < 0.15) { // 15% wrong color, correct letter
          tokenType = 'wrong-color';
          color = Phaser.Math.RND.pick(COLORS.filter(c => c !== gameData.current.currentTargetColor));
          letter = gameData.current.currentTargetLetter;
          isTarget = false;
        } else if (wrongChance < 0.25) { // 10% correct color, wrong letter
          tokenType = 'wrong-letter';
          color = gameData.current.currentTargetColor;
          letter = Phaser.Math.RND.pick(LETTERS.filter(l => l !== gameData.current.currentTargetLetter));
          isTarget = false;
        } else {
          // Normal random token
          color = Phaser.Math.RND.pick(COLORS);
          letter = Phaser.Math.RND.pick(LETTERS);
          isTarget = color === gameData.current.currentTargetColor && letter === gameData.current.currentTargetLetter;
        }
      }
      
      console.log('CREATING TOKEN:', { 
        color, letter, isTarget, tokenType, 
        targetColor: gameData.current.currentTargetColor, 
        targetLetter: gameData.current.currentTargetLetter 
      });
    }
    
    return {
      id: Math.random().toString(36),
      x,
      y,
      color,
      letter,
      isTarget,
      tokenType,
      bonusValue,
      size: tokenSize
    };
  };

  const spawnToken = () => {
    console.log('Attempting to spawn token, game state:', gameData.current.gameState);
    if (!gameScene.current || gameData.current.gameState !== 'playing') {
      console.log('Cannot spawn - scene or game state invalid');
      return;
    }

    const scene = gameScene.current;
    const padding = 60;
    const tokenRadius = 35;
    
    // Check if we need to force a target token
    const timeSinceLastTarget = scene.time.now - gameData.current.lastTargetSpawnTime;
    const pastDeadline = scene.time.now > gameData.current.lastTargetDeadline;
    const shouldForceTarget = gameData.current.mustShowTarget || pastDeadline || (timeSinceLastTarget > gameData.current.targetSpawnCooldown && !gameData.current.targetTokenOnScreen);
    
    console.log('Target spawn check:', {
      timeSinceLastTarget,
      pastDeadline,
      mustShowTarget: gameData.current.mustShowTarget,
      targetOnScreen: gameData.current.targetTokenOnScreen,
      shouldForceTarget
    });
    
    // Generate random position with overlap checking
    let x: number, y: number;
    let attempts = 0;
    const maxAttempts = 50;
    
    do {
      x = Phaser.Math.Between(padding + tokenRadius, 390 - padding - tokenRadius);
      y = Phaser.Math.Between(padding + 100 + tokenRadius, 844 - padding - tokenRadius);
      attempts++;
      
      // Check for overlap with existing tokens
      const tooClose = gameData.current.tokens.some(existingToken => {
        const distance = Math.sqrt(
          Math.pow(x - existingToken.x, 2) + Math.pow(y - existingToken.y, 2)
        );
        return distance < (tokenRadius * 2 + 20); // Minimum distance between tokens
      });
      
      if (!tooClose || attempts >= maxAttempts) break;
    } while (attempts < maxAttempts);
    
    console.log('Spawning token at:', x, y, 'after', attempts, 'attempts', shouldForceTarget ? '(FORCED TARGET)' : '');
    const token = createToken(x, y, shouldForceTarget);
    
    // Update target tracking
    if (token.isTarget) {
      gameData.current.lastTargetSpawnTime = scene.time.now;
      gameData.current.targetTokenOnScreen = true;
      gameData.current.mustShowTarget = false;
      gameData.current.lastTargetDeadline = scene.time.now + 5000; // Next target must appear within 5 seconds after this one is handled
      console.log('TARGET TOKEN SPAWNED:', {
        color: token.color,
        letter: token.letter,
        expectedColor: gameData.current.currentTargetColor,
        expectedLetter: gameData.current.currentTargetLetter,
        match: token.color === gameData.current.currentTargetColor && token.letter === gameData.current.currentTargetLetter
      });
    }
    
    // Create token graphics with progressive sizing
    const tokenSprite = scene.add.graphics();
    const radius = token.size || 35;
    
    // Special visual effects for bonus tokens
    if (token.tokenType === 'bonus-life' || token.tokenType === 'bonus-freeze' || token.tokenType === 'bonus-score') {
      // Add pulsing glow for bonus tokens
      const glow = scene.add.graphics();
      glow.fillStyle(Phaser.Display.Color.HexStringToColor(token.color).color, 0.3);
      glow.fillCircle(0, 0, radius + 10);
      glow.setPosition(token.x, token.y);
      
      scene.tweens.add({
        targets: glow,
        scaleX: 1.2,
        scaleY: 1.2,
        alpha: 0.1,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
    
    // Token background with glow effect
    tokenSprite.fillStyle(Phaser.Display.Color.HexStringToColor(token.color).color);
    tokenSprite.fillCircle(0, 0, radius);
    tokenSprite.setPosition(token.x, token.y);
    
    // Add border - special borders for different token types
    let borderColor = 0x666666;
    if (token.isTarget) {
      borderColor = Phaser.Display.Color.HexStringToColor(token.color).color;
    } else if (token.tokenType === 'wrong-color' || token.tokenType === 'wrong-letter') {
      borderColor = 0xFF4444; // Red border for tricky tokens
    } else if (token.tokenType.startsWith('bonus-')) {
      borderColor = 0xFFD700; // Gold border for bonus tokens
    }
    
    tokenSprite.lineStyle(3, borderColor);
    tokenSprite.strokeCircle(0, 0, radius);
    
    // Create text with appropriate size
    const fontSize = Math.max(16, Math.floor(radius * 0.7));
    const tokenText = scene.add.text(token.x, token.y, token.letter, {
      fontSize: `${fontSize}px`,
      fontFamily: 'Arial Black',
      color: '#ffffff',
      align: 'center'
    });
    tokenText.setOrigin(0.5, 0.5);
    
    // Make both sprite and text interactive
    tokenSprite.setInteractive(new Phaser.Geom.Circle(0, 0, radius), Phaser.Geom.Circle.Contains);
    tokenText.setInteractive();
    
    // Debug: Check if interactive was set correctly
    console.log('🔧 Setting up token interaction for:', token.id);
    console.log('🔧 Token position:', token.x, token.y, 'radius:', radius);
    
    // Set cursor to pointer for interactive elements
    if (tokenSprite.input) tokenSprite.input.cursor = 'pointer';
    if (tokenText.input) tokenText.input.cursor = 'pointer';
    
    token.sprite = tokenSprite;
    token.text = tokenText;
    
    // Add click handlers directly to the token objects
    const handleTokenClick = (event?: any) => {
      console.log('🎯 TOKEN CLICKED - Game is working!!!!!!!!!!!!!!!!!!!!!!!!!!!', {
        tokenId: token.id,
        tokenColor: token.color,
        tokenLetter: token.letter,
        isTarget: token.isTarget,
        expectedColor: gameData.current.currentTargetColor,
        expectedLetter: gameData.current.currentTargetLetter,
        timestamp: new Date().toISOString(),
        eventType: event?.type || 'unknown'
      });
      
      // Prevent event bubbling (only for DOM events)
      if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation();
      }
      
      // If this was a target token, mark it as handled
      if (token.isTarget) {
        gameData.current.targetTokenOnScreen = false;
        console.log('TARGET TOKEN CLICKED - Success!');
      }
      
      // Remove token immediately
      const tokenIndex = gameData.current.tokens.findIndex(t => t.id === token.id);
      if (tokenIndex !== -1) {
        gameData.current.tokens.splice(tokenIndex, 1);
        gameData.current.tokenStartTimes.delete(token.id);
        // Token state managed internally by Phaser game
      }
      
      // Destroy sprites
      tokenSprite.destroy();
      tokenText.destroy();
      
      // Handle different token types
      if (token.isTarget) {
        // Correct target tap
        const reactionTime = scene.time.now - (gameData.current.tokenStartTimes.get(token.id) || 0);
        const speedBonus = Math.max(1, Math.floor(5 - (reactionTime / 500)));
        
        // Consecutive hits multiplier
        gameData.current.consecutiveHits += 1;
        gameData.current.scoreMultiplier = Math.min(5, 1 + (gameData.current.consecutiveHits * 0.2)); // Max 5x multiplier
        
        const finalScore = Math.floor(speedBonus * gameData.current.scoreMultiplier);
        gameData.current.score += finalScore;
        gameData.current.tokensCollected += 1;
        setScore(gameData.current.score);
        
        // Update level and accelerating pace
        const newLevel = Math.floor(gameData.current.tokensCollected / 8) + 1;
        if (newLevel > gameData.current.level) {
          gameData.current.level = newLevel;
          const speedMultiplier = 1 - (newLevel * 0.1); // 10% faster each level
          gameData.current.spawnInterval = Math.max(200, Math.floor(gameData.current.spawnInterval * speedMultiplier));
          toast.info(`Level ${newLevel}! Faster tokens!`, {
            duration: 2000,
            position: "top-center"
          });
        }
        
        console.log('TARGET SCORE UPDATE:', {
          speedBonus,
          multiplier: gameData.current.scoreMultiplier,
          finalScore,
          consecutiveHits: gameData.current.consecutiveHits,
          newTotal: gameData.current.score
        });
        
        // Progress to next letter in VIVACLUB
        gameData.current.currentTargetIndex = (gameData.current.currentTargetIndex + 1) % VIVACLUB_SEQUENCE.length;
        gameData.current.currentTargetLetter = VIVACLUB_SEQUENCE[gameData.current.currentTargetIndex];
        gameData.current.currentTargetColor = TARGET_COLORS[gameData.current.currentTargetIndex];
        
        gameData.current.lastTargetDeadline = scene.time.now + 5000;
        gameData.current.mustShowTarget = true;
        
        setCurrentTargetIndex(gameData.current.currentTargetIndex);
        setCurrentTargetLetter(gameData.current.currentTargetLetter);
        setCurrentTargetColor(gameData.current.currentTargetColor);
        
        // Play success sound - higher pitch for higher multipliers
        const pitch = 400 + (gameData.current.scoreMultiplier * 100);
        playSound(pitch, 0.2, 0.15);
        
        const multiplierText = gameData.current.scoreMultiplier > 1 ? ` (${gameData.current.scoreMultiplier.toFixed(1)}x)` : '';
        toast.success(`+${finalScore} Point${finalScore > 1 ? 's' : ''}${multiplierText}! Next: ${gameData.current.currentTargetLetter}`, {
          duration: 1500,
          position: "top-center"
        });
        
      } else if (token.tokenType === 'bonus-life') {
        // Bonus life token
        gameData.current.lives = Math.min(5, gameData.current.lives + 1);
        setLives(gameData.current.lives);
        playSound(600, 0.3, 0.2); // High positive sound
        toast.success("Extra Life! ♥", { duration: 2000, position: "top-center" });
        
      } else if (token.tokenType === 'bonus-freeze') {
        // Freeze time token
        gameData.current.freezeTimeUntil = scene.time.now + 3000;
        playSound(300, 0.5, 0.15); // Low freeze sound
        toast.success("Time Frozen! ❄", { duration: 2000, position: "top-center" });
        
      } else if (token.tokenType === 'bonus-score') {
        // Score boost token
        const bonus = token.bonusValue || 5;
        gameData.current.score += bonus;
        setScore(gameData.current.score);
        playSound(500, 0.3, 0.2); // Medium bonus sound
        toast.success(`Bonus ${bonus} Points! ★`, { duration: 2000, position: "top-center" });
        
      } else {
        // Wrong tap (normal, wrong-color, wrong-letter)
        gameData.current.consecutiveHits = 0; // Reset multiplier
        gameData.current.scoreMultiplier = 1;
        gameData.current.lives -= 1;
        setLives(gameData.current.lives);
        
        if (gameData.current.lives <= 0) {
          endGame();
          toast.error("No lives left! Game Over", {
            duration: 2000,
            position: "top-center"
          });
        } else {
          let errorMessage = "Wrong token!";
          if (token.tokenType === 'wrong-color') {
            errorMessage = "Wrong color!";
          } else if (token.tokenType === 'wrong-letter') {
            errorMessage = "Wrong letter!";
          }
          
          playSound(150, 0.4, 0.2); // Low error sound
          toast.error(`${errorMessage} ${gameData.current.lives} ${gameData.current.lives === 1 ? 'life' : 'lives'} left`, {
            duration: 1500,
            position: "top-center"
          });
        }
      }
    };
    
    tokenSprite.on('pointerdown', handleTokenClick);
    tokenText.on('pointerdown', handleTokenClick);
    
    // Spawn animation
    tokenSprite.setScale(0);
    tokenText.setScale(0);
    
    scene.tweens.add({
      targets: [tokenSprite, tokenText],
      scaleX: 1,
      scaleY: 1,
      duration: 400,
      ease: 'Back.easeOut'
    });
    
    // Auto-remove after token lifetime - flash for 1 second
    const lifetime = 1000; // Fixed 1 second lifetime
    
    scene.time.delayedCall(lifetime, () => {
      console.log('Token auto-removing:', token.id);
      
      // Only check for missed target if token still exists and is still marked as target
      const stillExists = gameData.current.tokens.find(t => t.id === token.id);
      if (token.isTarget && stillExists && gameData.current.targetTokenOnScreen) {
        console.log('TARGET TOKEN MISSED - Game Over!');
        gameData.current.targetTokenOnScreen = false;
        endGame();
        toast.error("You missed the target token! Game Over", {
          duration: 3000,
          position: "top-center"
        });
      } else {
        // Just remove the token normally
        removeToken(token.id);
      }
    });
    
    // Store spawn time for speed calculation
    gameData.current.tokenStartTimes.set(token.id, scene.time.now);
    
    gameData.current.tokens.push(token);
    // Token state managed internally by Phaser game
  };

  const removeToken = (tokenId: string) => {
    if (!gameScene.current) return;
    
    const tokenIndex = gameData.current.tokens.findIndex(t => t.id === tokenId);
    if (tokenIndex === -1) return;
    
    const token = gameData.current.tokens[tokenIndex];
    
    // Immediately destroy sprites to prevent lingering tokens
    if (token.sprite) {
      token.sprite.destroy();
    }
    if (token.text) {
      token.text.destroy();
    }
    
    // Remove from arrays and maps
    gameData.current.tokens.splice(tokenIndex, 1);
    gameData.current.tokenStartTimes.delete(tokenId);
    // Token state managed internally by Phaser game
  };

  // handleTouch function is now integrated directly into Phaser scene

  const updateGame = (time: number, delta: number) => {
    // Check if we've passed the deadline for showing a target
    if (gameData.current.gameState === 'playing' && time > gameData.current.lastTargetDeadline && !gameData.current.targetTokenOnScreen) {
      console.log('DEADLINE PASSED - No target token appeared within 5 seconds! Game Over!');
      endGame();
      toast.error("Target token didn't appear in time! Game Over", {
        duration: 3000,
        position: "top-center"
      });
      return;
    }
    
    // Check if time is frozen
    const isFrozen = time < gameData.current.freezeTimeUntil;
    
    // Update spawn timer (only if not frozen)
    if (!isFrozen) {
      gameData.current.spawnTimer += delta;
    }
    
    //console.log('Update game - spawn timer:', gameData.current.spawnTimer, 'interval:', gameData.current.spawnInterval, 'frozen:', isFrozen);
    
    if (gameData.current.spawnTimer >= gameData.current.spawnInterval) {
      console.log('Time to spawn token!');
      // Allow up to 5 tokens max on screen for more challenge
      if (gameData.current.tokens.length < 5) {
        spawnToken();
      }
      gameData.current.spawnTimer = 0;
      // Faster spawn rate - adjusted based on level
      const levelSpeedBonus = Math.max(0.5, 1 - (gameData.current.level * 0.1));
      gameData.current.spawnInterval = Phaser.Math.Between(
        Math.floor(300 * levelSpeedBonus), 
        Math.floor(1000 * levelSpeedBonus)
      );
    }
  };

  const startGame = async () => {
    console.log('🎮 INTERNAL GAME STARTED - Calling parent onGameStart');
    
    // IMPORTANT: Call parent to register the play and deduct a life
    // Wait for it to complete before starting the game
    if (onGameStart) {
      await onGameStart();
    }
    
    console.log('🎮 GAME STARTED - Click detection is active!');
    const startTime = gameScene.current?.time.now || 0;
    
    // Generate fixed colors for VIVACLUB sequence
    const vivaClubColors = TARGET_COLORS.slice(0, 8); // Use first 8 colors for consistency
    const initialTargetColor = vivaClubColors[0]; // V gets first color
    const initialTargetLetter = VIVACLUB_SEQUENCE[0];
    
    gameData.current.score = 0;
    gameData.current.lives = 3;
    gameData.current.gameState = 'playing';
    gameData.current.tokens = [];
    gameData.current.spawnTimer = 0;
    gameData.current.spawnInterval = Phaser.Math.Between(300, 800); // Start with faster spawns
    gameData.current.lastTargetSpawnTime = 0;
    gameData.current.targetSpawnCooldown = 5000;
    gameData.current.speedMultiplier = 1;
    gameData.current.startTime = startTime;
    gameData.current.tokenStartTimes.clear();
    gameData.current.currentTargetIndex = 0;
    gameData.current.currentTargetLetter = initialTargetLetter;
    gameData.current.currentTargetColor = initialTargetColor;
    gameData.current.vivaClubColors = vivaClubColors;
    gameData.current.targetTokenOnScreen = false;
    gameData.current.targetMissed = false;
    gameData.current.mustShowTarget = true; // Force first target to appear
    gameData.current.lastTargetDeadline = startTime + 5000; // First target must appear within 5 seconds
    
    // Reset new game features
    gameData.current.consecutiveHits = 0;
    gameData.current.scoreMultiplier = 1;
    gameData.current.level = 1;
    gameData.current.tokensCollected = 0;
    gameData.current.baseTokenSize = 35;
    gameData.current.currentTokenSize = 35;
    gameData.current.freezeTimeUntil = 0;
    
    // Initialize simple audio system
    initializeAudio();
    
    console.log('GAME STARTED - Initial target:', {
      color: initialTargetColor,
      letter: initialTargetLetter,
      deadline: gameData.current.lastTargetDeadline
    });
    
    setScore(0);
    setLives(3);
    setGameState('playing');
    // Token state managed internally by Phaser game
    setCurrentTargetIndex(0);
    setCurrentTargetLetter(initialTargetLetter);
    setCurrentTargetColor(initialTargetColor);
    
    toast.success(`Game Started! Find ${initialTargetColor} ${initialTargetLetter} within 5 seconds!`, {
      duration: 3000,
      position: "top-center"
    });
  };

  const backToMenu = () => {
    gameData.current.gameState = 'waiting';
    setGameState('waiting');
    
    // Clear all tokens
    gameData.current.tokens.forEach(token => {
      token.sprite?.destroy();
      token.text?.destroy();
    });
    gameData.current.tokens = [];
    gameData.current.tokenStartTimes.clear();
    // Token state managed internally by Phaser game
    
    setScore(0);
    setLives(3);
  };

  const endGame = () => {
    // Prevent multiple calls to endGame
    if (gameData.current.gameState === 'gameOver') {
      console.log('🎮 endGame already called, skipping...');
      return;
    }
    
    console.log('🎮 Ending game with score:', gameData.current.score);
    gameData.current.gameState = 'gameOver';
    
    // Only call parent callback - parent will save to database
    if (gameData.current.score > 0) {
      console.log('🎮 VivaClubGame: Calling onGameEnd with score:', gameData.current.score);
      onGameEnd?.(gameData.current.score);
    }
    
    setGameState('gameOver');
    
    // Clear all tokens
    gameData.current.tokens.forEach(token => {
      token.sprite?.destroy();
      token.text?.destroy();
    });
    gameData.current.tokens = [];
    gameData.current.tokenStartTimes.clear();
    // Token state managed internally by Phaser game
  };

  useEffect(() => {
    if (!gameRef.current) return;

    console.log('Initializing Phaser game...');

    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: 390,
      height: 844,
      parent: gameRef.current,
      backgroundColor: '#1a1a1a',
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
      scene: {
        key: 'VivaClubGame',
        create: function() {
          console.log('Phaser scene created!');
          
          // Store scene reference
          gameScene.current = this;
          
          // Create background
          const bg = this.add.graphics();
          bg.fillGradientStyle(0x1a1a1a, 0x1a1a1a, 0x0a0a0a, 0x0a0a0a, 1);
          bg.fillRect(0, 0, 390, 844);

          // Add stars
          for (let i = 0; i < 15; i++) {
            const star = this.add.circle(
              Phaser.Math.Between(0, 390),
              Phaser.Math.Between(100, 744),
              1,
              0xA855F7,
              0.3
            );
            
            this.tweens.add({
              targets: star,
              alpha: 0.8,
              duration: 2000,
              yoyo: true,
              repeat: -1,
              delay: Phaser.Math.Between(0, 2000)
            });
          }

          console.log('Phaser scene setup complete, input system ready');
        },
        
        update: function(time: number, delta: number) {
          if (gameData.current.gameState === 'playing') {
            updateGame(time, delta);
          }
        }
      }
    };

    phaserGame.current = new Phaser.Game(config);
    
    console.log('Phaser game initialized');

    return () => {
      console.log('Destroying Phaser game');
      if (phaserGame.current) {
        phaserGame.current.destroy(true);
        phaserGame.current = null;
        gameScene.current = null;
      }
    };
  }, []);

  // Scores are now handled by backend - no localStorage needed

  const handleShowLeaderboard = () => {
    setShowLeaderboard(true);
  };

  return (
    <div className="relative w-full h-screen bg-viva-black overflow-hidden flex items-center justify-center">
      <div 
        ref={gameRef} 
        className="w-[390px] h-[844px] max-w-full max-h-full border border-viva-border rounded-lg overflow-hidden" 
      />
      
      <div className="absolute inset-0 max-w-[390px] max-h-[844px] mx-auto pointer-events-none">
        <GameHUD
          score={score}
          lives={lives}
          targetColor={currentTargetColor}
          targetLetter={currentTargetLetter}
          gameState={gameState}
          onStartGame={startGame}
          onRestartGame={startGame}
          onBackToMenu={backToMenu}
          onShowLeaderboard={handleShowLeaderboard}
          dailyPlaysRemaining={dailyPlaysRemaining}
          vivaClubProgress={VIVACLUB_SEQUENCE.map((letter, index) => ({
            letter,
            color: TARGET_COLORS[index] || '#666666',
            isCompleted: index < currentTargetIndex,
            isCurrent: index === currentTargetIndex
          }))}
        />
        
        <Leaderboard
          isOpen={showLeaderboard}
          onClose={() => setShowLeaderboard(false)}
          gameName="vivaTapsGoldRush"
        />
      </div>
    </div>
  );
};
